import * as ngCore from '@angular/core'
import type * as ents from '@attain-apps/entities'
import type * as router from '@attain-apps/fe/web/main/lib/router'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { Word } from '@attain-apps/string-builder'

import type * as assistants from '../../../../../assistants'
import type * as display from '../../../../../display'
import type * as me from '../../../../../me'
import type * as variableOverrides from '../../../../../variable-overrides'
import type * as currentRendering from '../../feature'
import type * as models from '../../models'

import { getActionsConfig } from './actions'
import { ApiService } from './api.service'
import * as effects from './effects'
import { Selectors } from './selectors'
import { State } from './state'

export class Feature extends store.Feature<
  State,
  Selectors,
  ReturnType<typeof getActionsConfig>
> {
  /** @see {@link ApiService} */
  public readonly api = ngCore.inject(ApiService)

  constructor(
    currentRenderingFeature: currentRendering.Feature,
    meFeature: me.Feature,
    toasterFeature: display.toaster.Feature,
    routerFeature: router.Router,
    variablesFeature: variableOverrides.Feature,
    assistantsFeature: assistants.Assistants,
  ) {
    super({
      actions: getActionsConfig(),
      initialState: State.INITIAL,
      name: 'Actions',
      parent: currentRenderingFeature,
      selectors: () => new Selectors(this),
    })

    this.registerEffects(
      effects.actionExecutions(currentRenderingFeature),
      effects.executeActionOnSubmit(),
      effects.executeUiActions(
        currentRenderingFeature,
        meFeature,
        toasterFeature,
        routerFeature,
        variablesFeature,
        assistantsFeature,
      ),
    )
  }

  /**
   * Creates {@link models.action.Handler} for given {@link ents.core.mosaic.Action.Ref}.
   *
   * __NOTE:__ This should be the only way you execute {@link ents.core.mosaic.Action.Ref actions} for the
   * current rendering, as the handler {@link models.action.Handler} encapsulates some important logic for
   * tracking the execution of the {@link link ents.core.mosaic.Action.Ref action}.
   *
   * @see {@link  models.action.Handler}
   */
  public createActionHandler(
    source: string,
    context: Omit<models.action.Handler.Context, 'id'>,
  ): models.action.Handler {
    const handlerId = Word.generate()
    return {
      context: { ...context, id: handlerId },
      execute: () => {
        this.actions.executeAction.from(source).execute({
          context: {
            id: handlerId,
            ...context,
          },
        })
      },
      isExecuting: this.selectors.actionIsExecuting({ handlerId }),
    }
  }
}
